import {html, HTMLTemplateResult, PropertyValues, TemplateResult} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {state} from 'lit/decorators/state.js';
import {repeat} from "lit/directives/repeat.js";
import {when} from "lit/directives/when.js";
import {StyleInfo, styleMap} from "lit/directives/style-map.js";
import {ClassInfo, classMap} from "lit/directives/class-map.js";

import {AbstractO2dElement, DomHelper, O2DCustomEvent} from "@o2d/shared";
import {areSectionsStartEndLinesEqual} from '../utils/are-sections-start-end-lines-equal.js';

import '@o2d/grid-layout';
import {
    AreaConfiguration,
    ExtendedSection,
    type LayoutTemplate,
    type LayoutSizeChangedDetail,
    O2dAreaElement,
    type O2dLayoutElement,
    ResolvedAreaConfiguration,
    ResolvedAxisConfiguration,
    ScrollEventDetail,
    Section,
    StartEndLines
} from '@o2d/grid-layout';
import {
    AxisConfiguration,
    AxisDataProviderFactory,
    AxisFactory,
    buildDataProviderId,
    CrosspointDataProvider,
    CrosspointDataProviderConfiguration,
    CrosspointDataServiceFactory,
    DefaultErrorHandler,
    Axis,
    SpatialMatrix,
    StandardDataProvider, Viewport
} from "@o2d/datasource";
import type {DataProviderStateManagerInterface} from '../state/data-provider-state-manager.interface';
import {DataProviderStateManager} from '../state/data-provider-state-manager';
import {DateTime} from "luxon";
import type {CursorChangedDetail} from './cursor-changed-detail.interface';
import type {DataProviderReadyDetail} from './data-provider-ready-detail.interface';

/**
 * The part of an axis the viewport preload needs: its own limits, and a range load.
 *
 * Structural on purpose. `Axis<TValue, TPrimitive>` is generic over the value type,
 * which neither of these two members involves, so naming the whole class here would
 * force a value type on a method that never touches one.
 */
type PreloadableAxis = {
    getBoundaries(): [number, number],
    ensureRange(startIndex: number, endIndex: number): Promise<void>
};

/** Pending CSS variable write to be applied after async data loading completes. */
type PendingCssWrite = {
    prop: string,
    value: string
};

type DataProviderBasedStep = {
    axisId: string,
    steps: number,
    /** Buffered CSS remainder writes to apply synchronously with content update. */
    pendingCssWrites: PendingCssWrite[],
    /**
     * The axis' relative delta as it was BEFORE this event advanced it.
     *
     * Kept so a load window can be undone: the stored delta has already moved on by
     * the time the load is awaited, so this is the only value left to fall back to.
     */
    previousRelativeDelta: number
};

/**
 * Represents a normalized grid layout section with specific configuration details.
 *
 * This type models a section of a grid layout, normalizing its properties for consistent usage.
 */
type NormalizedSection = {
    section: Section,
    name: string,
    axis: 'x' | 'y'
};

type AreaCursorIndexCalculationData = {
    areaConfiguration: ResolvedAreaConfiguration,
    axesGridSectionStartEndLines: {
        axisX: StartEndLines,
        axisY: StartEndLines
    }
};

/**
 * Structural CSS styles for o2d-timeline component.
 * Theme-agnostic: colors and visual styling are provided via CSS Custom Properties
 * that must be defined by the consuming application.
 */
const O2D_TIMELINE_STYLES = `
/* Component host styles - required for proper flex container behavior */
o2d-timeline {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
}

o2d-layout {
    display: grid;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}
`;

/**
 * Style element ID for deduplication
 */
const O2D_TIMELINE_STYLE_ID = 'o2d-timeline-styles';

/**
 * Represents a custom web component that creates a timeline element for the Open2Deploy (O2D) application.
 * This component is designed to display and manage complex timeline-based data within a customizable grid layout.
 */
@customElement('o2d-timeline')
export class O2dTimelineElement extends AbstractO2dElement {

    /**
     * Flag to track if styles have been injected into the document
     */
    private static stylesInjected = false;

    @property({type: Array})
    startCursors: { axisId: string, value: any }[] = [];

    @property({type: Object})
    layoutTemplate!: LayoutTemplate;

    private resolvedAxisConfigurations: ResolvedAxisConfiguration<any>[] | null = null;

    /**
     * Tracks StateManager version to trigger Lit reactive updates.
     * Incremented when the cursor position changes via scroll events.
     * Note: This property is intentionally only written to, as Lit's @state()
     * decorator uses property changes to schedule re-renders.
     */
    @state() private stateManagerVersion = 0;

    @property({type: Object})
    axisConfigurations!: AxisConfiguration<any, any>[];

    @property({type: Array})
    areaConfigurations: AreaConfiguration[] = [];

    /**
     * Configuration array for DataProviders
     *
     * Each configuration defines how a DataProvider is created including:
     * - axis IDs (axisIdX, axisIdY)
     * - item data service configuration (mock, axis-label, or custom)
     * - item mapper for transforming domain items
     * - optional error handler
     *
     * The configuration is looked up by buildDataProviderId(axisIdX, axisIdY).
     */
    @property({type: Array})
    dataProviderConfigurations: CrosspointDataProviderConfiguration<any, any, any>[] = [];

    /**
     * A Map object that associates a string key with an object representing the start and end line numbers
     * of a grid section. Each entry in the map specifies the range of lines that define a particular grid section.
     *
     * The string key typically represents a unique identifier for the grid section.
     *
     * Each value in the map is an object containing:
     * - `startLine`: The starting line number of the grid section.
     * - `endLine`: The ending line number of the grid section.
     */
    @state()
    private sectionsStartEndLines = new Map<string, { startLine: number, endLine: number }>();

    /**
     * Manages the state of a data provider by handling state updates, retrieval, and persistence.
     *
     * This variable serves as an instance of the `DataProviderStateManagerInterface`
     * implementation, allowing interaction with the underlying data provider state.
     * Ensures consistent state management and integrates with the data provider to
     * provide a seamless data access and update experience.
     *
     * Responsibilities include:
     * - Handling changes to the data provider state.
     * - Providing methods to access current state information.
     * - Persisting state changes to ensure consistency across sessions.
     */
    private readonly dataProviderStateManager: DataProviderStateManagerInterface<DateTime, unknown, unknown> = new DataProviderStateManager();

    /**
     * Initial relative delta value.
     * 0.9999 positions viewport just before next integer boundary,
     * so the next scroll step triggers immediately.
     */
    private static readonly INITIAL_RELATIVE_DELTA = 0.9999;

    /**
     * Maximum relative delta value.
     * 0.0001 positions viewport just after the integer boundary,
     * preventing further scroll in the max direction.
     */
    private static readonly MAX_RELATIVE_DELTA = 0.0001;

    /**
     * Current scroll state (relative delta of cell size)
     */
    private relativeDeltasByAxisId = new Map<string, number>();

    /**
     * Tracks pending ensureRange() promises per axis to prevent race conditions
     * during rapid scrolling. Key format: "${axisId}-${axis}"
     */
    private pendingEnsureRangePromises = new Map<string, Promise<void>>();

    /**
     * Per axis (`${axisId}-${axis}`): the newest buffered remainder writes of ANY
     * scroll event, held back while a row change of that axis is still loading.
     *
     * The newest writes win because they describe the position the input has
     * actually reached; an earlier event's values are stale by the time the load
     * returns, and writing them is the second half of the scroll jump.
     */
    private remainderSlotByAxisKey = new Map<string, PendingCssWrite[]>();

    /**
     * Per axis: the row changes collected while that axis' load window is open.
     *
     * The cursor is moved by this sum once, when the window closes, instead of
     * once per event. While the window is open the cursor does not move at all,
     * so no frame can paint a cursor and a remainder that disagree.
     */
    private collectedStepsByAxisKey = new Map<string, number>();

    /**
     * Per axis: how many scroll events are currently waiting inside the window.
     *
     * A counter, not the id of the newest event: the closing pass must also be the
     * pass after which all data is present, and the newest event alone does not
     * establish that. Measured on the alternative: a sequence number commits the
     * cursor while a load is still open, which is the state the await exists to
     * prevent.
     */
    private openStepWindowCountByAxisKey = new Map<string, number>();

    /**
     * Per axis: the relative delta as it was before the window's first event
     * advanced it, kept so a rejected load can be undone.
     */
    private windowDeltaSnapshotByAxisKey = new Map<string, number>();

    /** Axes whose current window saw a rejected range load. */
    private poisonedStepWindowAxisKeys = new Set<string>();

    /**
     * Guards against overlapping applyCursorChangesAsync invocations.
     * Rapid startCursors changes serialize via this promise.
     */
    private _pendingCursorApply: Promise<void> | null = null;

    /**
     * Promise that resolves when all axes are fully initialized.
     * This ensures finite axes have loaded their data before rendering.
     * Undefined until axes are created in willUpdate().
     */
    private axesReadyPromise: Promise<void[]> | undefined;

    /**
     * State flag indicating whether all axes are fully initialized.
     * Areas will only be rendered when this is true.
     */
    @state() private axesReady = false;

    /**
     * Monotonically increasing counter that tags every axis-swap cycle (PRD-008).
     *
     * Each `willUpdate()` `needsFullRebuild` branch reads `++_swapGeneration`
     * into a local `gen`, then passes it to `_runAxisSwapAsync(gen, ...)`. After
     * every `await` checkpoint inside the swap, the async sequence compares the
     * captured `gen` against the current `_swapGeneration`; if they differ, a
     * newer swap has started and the stale one MUST abort to keep state
     * consistent. Plain instance field — NOT `@state` — because changes do not
     * need to trigger Lit re-renders.
     */
    private _swapGeneration = 0;

    /**
     * Resolvers waiting for a specific swap generation to complete (PRD-008 G3).
     *
     * Populated by `setAxisConfigurationsAndWait()` keyed by the `_swapGeneration`
     * that the property assignment triggers. After `_runAxisSwapAsync` finishes
     * step 9 (data-provider-ready dispatch), it looks up its `gen` here, calls
     * the resolver and removes the entry. On disconnect, all open resolvers
     * MUST be rejected and the map cleared (Failure Condition #5: memory leak).
     */
    private _swapCompletedResolvers: Map<number, () => void> = new Map();

    /**
     * indexed GridSection aus Sicht des GridLayoutTemplates
     * @private
     */
    private normalizedSectionById = new Map<string, NormalizedSection>();

    // TODO: der muss möglichst am Ende austauschbar sein
    private readonly defaultErrorHandler = new DefaultErrorHandler();

    private resolvedAreaConfigurations: ResolvedAreaConfiguration[] | null = null;

    private areaCursorIndexMaps = {
        axisX: new Map<string, number>(),
        axisY: new Map<string, number>()
    };

    /**
     * Injects structural CSS styles into the document head.
     * Uses static flag to ensure styles are only injected once per document.
     */
    public override connectedCallback(): void {
        super.connectedCallback();
        this.injectStyles();
    }

    public override disconnectedCallback(): void {
        super.disconnectedCallback();
        this.forEachDataProvider(dataProvider => dataProvider.dispose());

        // PRD-008 T4.3: reject every pending setAxisConfigurationsAndWait() so consumers
        // are notified that the swap will never complete on a torn-down element. The map
        // is cleared afterwards to free the captured resolver closures (Failure Condition #5).
        if (this._swapCompletedResolvers.size > 0) {
            const pending = Array.from(this._swapCompletedResolvers.values());
            this._swapCompletedResolvers.clear();
            for (const resolver of pending) {
                // Resolvers are wrapped by setAxisConfigurationsAndWait() to also support rejection.
                (resolver as unknown as { reject?: (err: Error) => void }).reject?.(new Error('disconnected'));
            }
        }
    }

    /**
     * Public swap-and-wait API (PRD-008 G3).
     *
     * Assigns the given axis configurations and returns a Promise that resolves
     * only after the entire `_runAxisSwapAsync()` sequence has completed for the
     * generation that this assignment triggered (axes ready, preload done, all
     * `data-provider-ready` events dispatched). Useful for orchestration code
     * that must coordinate multiple atomic swaps without racing against Lit's
     * reactive update lifecycle.
     *
     * Pre-conditions: `layoutTemplate` and a non-empty `areaConfigurations`
     * must already be set; otherwise the returned Promise rejects synchronously
     * with the documented error message.
     *
     * Timeout: 5000 ms — protects callers from a deadlocked swap (e.g. an axis
     * whose `ready` Promise never settles). On timeout the resolver is removed
     * from the map and the Promise rejects.
     *
     * Disconnect: see `disconnectedCallback()` — pending waits reject with
     * `Error('disconnected')`.
     */
    public setAxisConfigurationsAndWait(configs: AxisConfiguration<any, any>[]): Promise<void> {
        if (!this.layoutTemplate || !this.areaConfigurations || this.areaConfigurations.length === 0) {
            return Promise.reject(new Error('setAxisConfigurationsAndWait requires layoutTemplate + areaConfigurations to be set'));
        }

        // Lit assigns `axisConfigurations` synchronously and schedules an async
        // update that calls `willUpdate()`, which increments `_swapGeneration`
        // exactly once for the rebuild branch. Predict that next value here so
        // we can register the resolver before the property assignment fires.
        const expectedGen = this._swapGeneration + 1;

        return new Promise<void>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                this._swapCompletedResolvers.delete(expectedGen);
                reject(new Error('setAxisConfigurationsAndWait timed out after 5000ms'));
            }, 5000);

            const wrappedResolve: (() => void) & { reject?: (err: Error) => void } = () => {
                clearTimeout(timeoutId);
                resolve();
            };
            // Disconnect cleanup path uses .reject?.() on the resolver function.
            wrappedResolve.reject = (err: Error) => {
                clearTimeout(timeoutId);
                reject(err);
            };

            this._swapCompletedResolvers.set(expectedGen, wrappedResolve);

            this.axisConfigurations = configs;
        });
    }

    /**
     * Injects the structural CSS styles into the document head if not already present.
     * Styles are injected once per document and shared across all o2d-timeline instances.
     */
    private injectStyles(): void {
        if (O2dTimelineElement.stylesInjected) {
            return;
        }

        // Check if styles already exist (e.g., from SSR or previous session)
        if (document.getElementById(O2D_TIMELINE_STYLE_ID)) {
            O2dTimelineElement.stylesInjected = true;
            return;
        }

        const styleElement = document.createElement('style');
        styleElement.id = O2D_TIMELINE_STYLE_ID;
        styleElement.textContent = O2D_TIMELINE_STYLES;
        document.head.appendChild(styleElement);

        O2dTimelineElement.stylesInjected = true;
    }

    /**
     * Execute a callback function for each registered DataProvider
     *
     * @param callback - Function to execute for each DataProvider
     */
    private forEachDataProvider(callback: (dataProvider: CrosspointDataProvider<DateTime, unknown, unknown>) => void): void {
        for (const key of this.dataProviderStateManager.getAllProviderKeys()) {
            const provider = this.dataProviderStateManager.getProvider(key);
            if (provider) {
                callback(provider);
            }
        }
    }

    /**
     * Whether the spans of a data provider can be recomputed from the current grid data.
     *
     * `calculateAreaCursorIndexesByDataProvider()` looks every area of the provider up in
     * both `sectionsStartEndLines` and `normalizedSectionById` with a non-null assertion,
     * so a missing entry throws. Inside the axis-swap sequence that throw is caught by the
     * surrounding try/catch and would suppress every `data-provider-ready` event, leaving
     * the consumer without providers at all. Two states produce it: no layout has been
     * measured yet, and a rebuild that also changes `layoutTemplate`, whose new section ids
     * are absent from the grid lines until the layout reports them.
     *
     * @param dataProviderId provider whose areas are checked
     * @return true when every area of the provider resolves in both maps
     */
    private canRecomputeSpansOfDataProvider(dataProviderId: string): boolean {
        const areasOfDataProvider = this.resolvedAreaConfigurations
            ?.filter(areaConfiguration => areaConfiguration.dataProviderId === dataProviderId) ?? [];

        if (areasOfDataProvider.length === 0) {
            return false;
        }

        return areasOfDataProvider.every(areaConfiguration => {
            const {columnSectionId, rowSectionId} = areaConfiguration.sectionIds;
            return this.sectionsStartEndLines.has(columnSectionId)
                && this.sectionsStartEndLines.has(rowSectionId)
                && this.normalizedSectionById.has(columnSectionId)
                && this.normalizedSectionById.has(rowSectionId);
        });
    }

    /**
     * Re-clamps the cursor of every provider against its axis boundaries.
     *
     * At registration the axis boundaries are still unknown (lazy discovery via
     * fetchValues), so the cursor cannot be placed yet. Once the axes report their
     * boundaries, this re-runs the clamp so a finite axis starts at its minimum
     * boundary when all of its entries fit into the viewport.
     *
     * It passes each provider's STORED span back into `updateSpan()` and therefore
     * cannot repair a wrong span — with a span of 0 the "everything fits" branch is
     * unreachable and every cursor clamps to the axis origin. Supplying the span is
     * the job of `calculateAreaCursorIndexesByDataProvider()`, which has to have run
     * before this (see the rebuild path in `_runAxisSwapAsync()`).
     *
     * @private
     */
    private recalculateCursorsAfterAxesReady(): void {
        for (const key of this.dataProviderStateManager.getAllProviderKeys()) {
            const state = this.dataProviderStateManager.getState(key);
            if (state) {
                // updateSpan() recalculates cursor positions based on current
                // axis boundaries and re-clamps if needed
                this.dataProviderStateManager.updateSpan(key, state.spanX, state.spanY);
            }
        }
    }

    /**
     * Runs the post-rebuild swap sequence sequentially and atomically (PRD-008).
     *
     * Replaces the legacy `axesReadyPromise.then()` cascade in `willUpdate()` so
     * that `data-provider-ready` events are dispatched only after the axes have
     * fully initialized AND the new viewport data has been preloaded. Older
     * concurrent swaps are aborted via the `_swapGeneration` StaleGuard, and any
     * await checkpoint that observes a disconnected element returns immediately
     * to avoid touching torn-down state.
     *
     * Sequence (PRD-008 §"Interne Swap-Sequenz"):
     *   1. await `axesReadyPromise`  →  StaleGuard + Disconnect-Guard
     *   2. recalculateCursorsAfterAxesReady()
     *   3. axesReady = true (opens the area-rendering gate)
     *   4. await preloadDataForCurrentViewportAsync()  →  re-check guards
     *   5. optionally await applyCursorChangesAsync()  →  re-check guards
     *   6. dispatch one `data-provider-ready` event per entry in `newProviderInfos`
     *
     * @param gen captured `_swapGeneration` value tagging this swap cycle
     * @param hasCursorChange whether `startCursors` changed alongside the rebuild
     * @param newProviderInfos providers freshly registered by `buildAllDataProviders()`
     */
    private async _runAxisSwapAsync(
        gen: number,
        hasCursorChange: boolean,
        newProviderInfos: { key: string, provider: CrosspointDataProvider<any, any, any> }[]
    ): Promise<void> {
        try {
            // Yield one microtask so any synchronous follow-up in the caller
            // (e.g. assigning a fresh `axesReadyPromise`) finishes before we
            // read the field below. Tests rely on this to inject a deferred.
            await Promise.resolve();
            if (this._isStaleSwap(gen) || !this.isConnected) return;

            const ready = this.axesReadyPromise;
            if (ready) {
                await ready;
                if (this._isStaleSwap(gen) || !this.isConnected) return;
            }

            // The providers were just registered with span 0: buildAllDataProviders()
            // has no grid measurements available at registration time. Recompute their
            // spans from the grid lines BEFORE the cursors are recalculated, because
            // recalculateCursorsAfterAxesReady() feeds the stored span back into
            // updateSpan() — with a span of 0 every cursor clamps to the axis origin and
            // the rows above it become unreachable. Nothing else recomputes the spans
            // after this kind of rebuild: the only other call site depends on a
            // `sectionsStartEndLines` change, which a pure axis rebuild does not produce.
            for (const info of newProviderInfos) {
                if (this.canRecomputeSpansOfDataProvider(info.key)) {
                    this.calculateAreaCursorIndexesByDataProvider(this.resolvedAreaConfigurations!, info.key);
                } else {
                    console.warn(`[o2d-timeline] span of data provider "${info.key}" was not recomputed after the `
                        + 'axis rebuild: the grid lines of its areas are not available yet. Rows beyond the viewport '
                        + 'size stay unreachable until the next layout change.');
                }
            }

            this.recalculateCursorsAfterAxesReady();
            this.axesReady = true;

            await this.preloadDataForCurrentViewportAsync();
            if (this._isStaleSwap(gen) || !this.isConnected) return;

            if (hasCursorChange) {
                await this.applyCursorChangesAsync(this.startCursors);
                if (this._isStaleSwap(gen) || !this.isConnected) return;
            }

            for (const info of newProviderInfos) {
                this.dispatchEvent(new CustomEvent<DataProviderReadyDetail>('data-provider-ready', {
                    bubbles: true,
                    composed: true,
                    detail: { key: info.key, provider: info.provider }
                }));
            }

            // PRD-008 G3: signal callers awaiting setAxisConfigurationsAndWait()
            // for this generation that the swap fully completed.
            const resolver = this._swapCompletedResolvers.get(gen);
            if (resolver) {
                this._swapCompletedResolvers.delete(gen);
                resolver();
            }
        } catch (err) {
            console.error('[o2d-timeline] _runAxisSwapAsync failed', err);
        }
    }

    /**
     * Returns true when a newer swap has superseded `gen` and the current run
     * must abort to avoid mutating state that belongs to a later swap.
     */
    private _isStaleSwap(gen: number): boolean {
        return this._swapGeneration !== gen;
    }

    protected override shouldUpdate(changedProperties: PropertyValues): boolean {
        return !!this.layoutTemplate
            && (Array.isArray(this.axisConfigurations) && this.axisConfigurations.length > 0)
            && (Array.isArray(this.areaConfigurations) && this.areaConfigurations.length > 0)
            && super.shouldUpdate(changedProperties);
    }

    protected override willUpdate(changedProperties: PropertyValues): void {

        // baue Index alle GridSection nach GridSectionID auf
        if (changedProperties.has('layoutTemplate')) {
            this.normalizedSectionById = this.buildNormalizedSectionsById(this.layoutTemplate);
        }

        const hasConfigChange = changedProperties.has('axisConfigurations') || changedProperties.has('layoutTemplate') ||
            changedProperties.has('sectionsStartEndLines') || changedProperties.has('areaConfigurations') ||
            changedProperties.has('dataProviderConfigurations');
        const hasCursorChange = changedProperties.has('startCursors');

        if (hasConfigChange || hasCursorChange) {
            // Guard: Skip processing if required configurations are not yet available.
            // This prevents timing issues when properties are set asynchronously.
            if (!this.axisConfigurations || !this.layoutTemplate || this.sectionsStartEndLines.size < 1 ||
                !this.startCursors || this.startCursors.length === 0) {
                return;
            }

            // Distinguish between structural config changes (need full rebuild) and
            // sectionsStartEndLines-only changes (just update spans, no axis rebuild).
            const hasStructuralConfigChange = changedProperties.has('axisConfigurations') || changedProperties.has('layoutTemplate') ||
                changedProperties.has('areaConfigurations') || changedProperties.has('dataProviderConfigurations');
            const hasSectionsOnlyChange = changedProperties.has('sectionsStartEndLines') && !hasStructuralConfigChange;

            // Full rebuild needed when:
            // 1. Structural config changed (axis/area/provider/layout configs), OR
            // 2. No providers exist yet (first initialization)
            // Skip full rebuild when only sectionsStartEndLines changed AND providers already exist —
            // this happens when o2d-layout resizes (e.g. scrollbar appearing) and recalculates grid lines.
            const needsFullRebuild = hasStructuralConfigChange || !this.dataProviderStateManager.hasAnyProvider();
            const skipRebuildForSectionsOnly = hasSectionsOnlyChange && this.dataProviderStateManager.hasAnyProvider();

            if (needsFullRebuild && !skipRebuildForSectionsOnly) {
                // Tag this swap with a monotonically increasing generation (PRD-008).
                // The generation is captured by _runAxisSwapAsync to detect stale runs:
                // a later swap will increment _swapGeneration, causing earlier in-flight
                // swap awaits to abort via _isStaleSwap(gen) before mutating state.
                const gen = ++this._swapGeneration;

                // Dispose stale providers before rebuilding to prevent state leaks.
                this.dataProviderStateManager.clear();

                this.resolvedAxisConfigurations = this.generateAllResolvedAxisConfigurations(this.axisConfigurations, this.areaConfigurations, this.layoutTemplate);
                this.resolvedAreaConfigurations = this.generateAllResolvedAreaConfigurations(this.areaConfigurations, this.axisConfigurations);
                const axes = this.buildAxes(this.axisConfigurations);
                // Store promise for waiting on axes to be ready before preloading data
                this.axesReadyPromise = Promise.all(Array.from(axes.values()).map(a => a.ready));
                const newProviderInfos = this.buildAllDataProviders(this.resolvedAreaConfigurations, axes);

                // Synchronously close the area-rendering gate before any await so that
                // `when(axesReady, ...)` does not paint with the freshly cleared providers.
                // The gate is reopened inside _runAxisSwapAsync() once axes are ready.
                this.axesReady = false;

                // Run the atomic swap sequence (await axesReadyPromise, recalc cursors,
                // preload data, optionally apply cursor changes, dispatch data-provider-ready).
                // StaleGuard via `gen` ensures rapid successive swaps cancel earlier runs.
                void this._runAxisSwapAsync(gen, hasCursorChange, newProviderInfos);
            } else if (hasCursorChange) {
                // Cursor-only path: move viewport without destroying axes or providers.
                // Only reached when providers already exist and no config property changed.
                void this.applyCursorChangesAsync(this.startCursors);
            }
        }

        if (changedProperties.has('sectionsStartEndLines') && this.sectionsStartEndLines.size > 0 && !!this.resolvedAreaConfigurations) {

            for (const dataProviderKey of this.dataProviderStateManager.getAllProviderKeys()) {
                this.calculateAreaCursorIndexesByDataProvider(this.resolvedAreaConfigurations, dataProviderKey);
            }
        }
    }

    /**
     * Calculates and updates the area cursor indexes for a specific data provider based on its configurations.
     * It adjusts indexes for both TXAxisValue-axis and TYAxisValue-axis by processing the grid section start and end lines
     * associated with the area configurations of the given data provider.
     *
     * @param {AreaCursorIndexCalculationData[]} resolvedAreaConfigurations - Array of area configuration data which contains grid area details,
     *                                                                               axis information, and associated data provider identifiers.
     * @param {string} dataProviderId - Identifier for the data provider whose area cursor indexes need to be calculated and updated.
     * @return {void} This method does not return a value. It updates the area cursor indexes directly within the object's state.
     */
    private calculateAreaCursorIndexesByDataProvider(resolvedAreaConfigurations: ResolvedAreaConfiguration[], dataProviderId: string): void {

        /**
         * Prepares calculation data for determining cursor indexes of all grid areas.
         *
         * For each DataProvider, cursor indexes must be calculated that indicate
         * at which position within the data matrix a specific grid area begins.
         *
         * This transformation maps each area configuration to a data object that:
         * - contains the original area configuration
         * - references the start/end lines of the associated grid sections for both axes (TXAxisValue and TYAxisValue)
         *
         * This preprocessing avoids repeated map lookups during cursor index
         * calculation, which improves performance.
         */
        const areaConfigurationsOfDataProvider = resolvedAreaConfigurations
            .filter(areaConfiguration => areaConfiguration.dataProviderId == dataProviderId)
            .map(areaConfiguration => {
                return {
                    areaConfiguration,
                    axesGridSectionStartEndLines: {
                        axisX: this.sectionsStartEndLines.get(areaConfiguration.sectionIds.columnSectionId)!,
                        axisY: this.sectionsStartEndLines.get(areaConfiguration.sectionIds.rowSectionId)!
                    }
                } as AreaCursorIndexCalculationData
            });

        let newCursorIndexX = 0;
        let oldStartLineX = 0;
        let oldSpanX = 0;
        let typeOfLastGridSectionOfDataProviderX: 'fix' | 'fluid' = 'fix';

        areaConfigurationsOfDataProvider.sort((a1, a2) => a1.axesGridSectionStartEndLines.axisX.startLine - a2.axesGridSectionStartEndLines.axisX.startLine);

        for (const areaConfigurationOfDataProvider of areaConfigurationsOfDataProvider) {

            // TXAxisValue-Axis
            const gridSectionStartEndLinesNewX = areaConfigurationOfDataProvider.axesGridSectionStartEndLines.axisX;
            const newStartLineX = gridSectionStartEndLinesNewX.startLine;

            if (oldStartLineX != newStartLineX) {
                newCursorIndexX += (oldSpanX - (typeOfLastGridSectionOfDataProviderX == 'fluid' ? 1 : 0));
            }

            this.areaCursorIndexMaps.axisX.set(areaConfigurationOfDataProvider.areaConfiguration.areaId, newCursorIndexX);
            oldSpanX = gridSectionStartEndLinesNewX.endLine - gridSectionStartEndLinesNewX.startLine;
            oldStartLineX = newStartLineX;

            const gridSection = this.normalizedSectionById.get(areaConfigurationOfDataProvider.areaConfiguration.sectionIds.columnSectionId)!;
            typeOfLastGridSectionOfDataProviderX = gridSection.section.type;
        }

        const spanOfProviderX = newCursorIndexX + oldSpanX;

        let newCursorIndexY = 0;
        let oldStartLineY = 0;
        let oldSpanY = 0;
        let typeOfLastGridSectionOfDataProviderY: 'fix' | 'fluid' = 'fix';

        areaConfigurationsOfDataProvider.sort((a1, a2) => a1.axesGridSectionStartEndLines.axisY.startLine - a2.axesGridSectionStartEndLines.axisY.startLine);
        for (const areaConfigurationOfDataProvider of areaConfigurationsOfDataProvider) {

            // TYAxisValue-Axis
            const gridSectionStartEndLinesNewY = areaConfigurationOfDataProvider.axesGridSectionStartEndLines.axisY;
            const newStartLineY = gridSectionStartEndLinesNewY.startLine;

            if (oldStartLineY != newStartLineY) {
                newCursorIndexY += (oldSpanY - (typeOfLastGridSectionOfDataProviderY == 'fluid' ? 1 : 0));
            }

            this.areaCursorIndexMaps.axisY.set(areaConfigurationOfDataProvider.areaConfiguration.areaId, newCursorIndexY);
            oldSpanY = gridSectionStartEndLinesNewY.endLine - gridSectionStartEndLinesNewY.startLine;
            oldStartLineY = newStartLineY;

            const gridSection = this.normalizedSectionById.get(areaConfigurationOfDataProvider.areaConfiguration.sectionIds.rowSectionId)!;
            typeOfLastGridSectionOfDataProviderY = gridSection.section.type;
        }

        const spanOfProviderY = newCursorIndexY + oldSpanY;

        this.dataProviderStateManager.updateSpan(dataProviderId, spanOfProviderX, spanOfProviderY);
    }

    /**
     * Preload data for the current viewport using StateManager.
     *
     * Proactively loads data into a cache without waiting for render.
     * Improves perceived performance by reducing loading delays.
     */
    private async preloadDataForCurrentViewportAsync(): Promise<void> {
        if (!this.dataProviderStateManager.hasAnyProvider()) {
            return;
        }

        // Preload data for all providers using viewport from StateManager
        for (const providerId of this.dataProviderStateManager.getAllProviderKeys()) {
            const viewportRange = this.dataProviderStateManager.getViewport(providerId);
            const dataProvider = this.dataProviderStateManager.getProvider(providerId);

            if (!dataProvider) continue;

            const viewport: Viewport = {
                indexRangeX: [viewportRange.x.start, viewportRange.x.end],
                indexRangeY: [viewportRange.y.start, viewportRange.y.end]
            };

            // The WIDER axis ranges first, then the viewport.
            //
            // The order is the cost. `getViewportItemsForViewportAsync` awaits
            // `ensureRange` for exactly the visible rows, and a widening call after
            // it is a SECOND serial round trip — measured as two calls, [0,10] then
            // [-10,20]. Since the wide range is a superset, asking for it first
            // leaves the viewport's own call with nothing left to fetch, so the whole
            // preload costs one round trip instead of two. `data-provider-ready` is
            // dispatched after this method returns, so the difference is visible to
            // consumers whenever the axis data service is slow.
            //
            // Only the axis ranges are widened, deliberately. The scroll path awaits
            // `loadedBoundaries` and nothing else; cell data is fetched in the
            // background and never awaited, so a wider viewport would multiply that
            // background load without bringing the first frame forward. The
            // consequence, stated plainly: the preloaded rows have axis values but no
            // cell data, so scrolling into them shows them briefly empty. That is not
            // a regression, and it is not what this addresses.
            //
            // The widening is what keeps the FIRST gesture after loading from opening
            // a range load in the middle of the scroll path, which is what made that
            // gesture jump.
            await Promise.all([
                this.preloadAxisRangeBeyondViewportAsync(dataProvider.axisX, viewportRange.x.start, viewportRange.x.end),
                this.preloadAxisRangeBeyondViewportAsync(dataProvider.axisY, viewportRange.y.start, viewportRange.y.end),
            ]);

            await (dataProvider as any).getViewportItemsForViewportAsync(viewport);
        }
    }

    /**
     * Loads one axis beyond the visible range, in both directions.
     *
     * The buffer mirrors the one the scroll path itself uses, so a gesture that
     * stays inside it opens no load at all.
     *
     * @param axisInstance - the axis to widen
     * @param visibleStart - first visible index
     * @param visibleEnd - last visible index, INCLUSIVE (`ensureRange` takes an exclusive end)
     */
    private async preloadAxisRangeBeyondViewportAsync(
        axisInstance: PreloadableAxis | undefined,
        visibleStart: number,
        visibleEnd: number
    ): Promise<void> {
        if (!axisInstance) {
            return;
        }

        const span = visibleEnd - visibleStart;
        const buffer = Math.max(10, span);

        // Clamped to the axis' own limits, so a finite axis is not asked for rows
        // that cannot exist.
        const [absoluteMin, absoluteMax] = axisInstance.getBoundaries();
        const rangeStart = Number.isFinite(absoluteMin)
            ? Math.max(visibleStart - buffer, absoluteMin)
            : visibleStart - buffer;
        const rangeEnd = Number.isFinite(absoluteMax)
            ? Math.min(visibleEnd + buffer + 1, absoluteMax + 1)
            : visibleEnd + buffer + 1;

        if (rangeStart >= rangeEnd) {
            return;
        }

        await axisInstance.ensureRange(rangeStart, rangeEnd);
    }

    protected render(): TemplateResult {
        const templateName = this.layoutTemplate?.name ?? 'error';
        return html`
            <o2d-layout
                    template=${templateName}
                    .layoutTemplate=${this.layoutTemplate}
                    @layout-connected=${this.handleLayoutConnectedAsync}
                    @layout-scrolled=${this.handleLayoutScrolledAsync}
                    @layout-size-changed=${this.handleLayoutSizeChanged}>
                ${when(this.sectionsStartEndLines.size > 0 && this.axesReady, () =>
                        repeat(
                                this.resolvedAreaConfigurations ?? [],
                                resolvedAreaConfiguration => resolvedAreaConfiguration.areaId,
                                this.renderArea.bind(this, this.sectionsStartEndLines)
                        )
                )}
            </o2d-layout>
        `;
    }

    /**
     * Ensures axis data is loaded for the new viewport after scrolling.
     * Calls ensureRange() only if the new range exceeds loadedBoundaries.
     *
     * This method is called BEFORE cursor updates to ensure index↔value
     * mappings are available in the axis cache when needed.
     *
     * @param axisId - The identifier of the axis to check
     * @param axis - Which axis direction ('x' or 'y')
     * @param steps - Number of steps to scroll
     */
    private async ensureAxisRangeForScrollAsync(
        axisId: string,
        axis: 'x' | 'y',
        steps: number
    ): Promise<void> {
        // Find any provider using this axis to get axis instance
        const providersUsingAxis = this.dataProviderStateManager.findDataProviderByAxisId(axisId);
        if (providersUsingAxis.length === 0) return;

        const firstProvider = providersUsingAxis[0];
        const axisInstance = axis === 'x' ? firstProvider.axisX : firstProvider.axisY;

        if (!axisInstance) return;

        const key = `${axisId}-${axis}`;

        // Wait for pending request for this axis (if any) to prevent race conditions
        const pendingPromise = this.pendingEnsureRangePromises.get(key);
        if (pendingPromise) {
            await pendingPromise;
        }

        // Get state from first provider (all providers sharing the axis have same cursor)
        const state = this.dataProviderStateManager.getState(firstProvider.dataProviderKey);
        if (!state) return;

        const cursorIndex = axis === 'x' ? state.cursorIndexX : state.cursorIndexY;
        const span = axis === 'x' ? state.spanX : state.spanY;

        const newCursorIndex = cursorIndex + steps;
        const newEndIndex = newCursorIndex + span;

        const [loadedMin, loadedMax] = axisInstance.loadedBoundaries;

        // Check if new viewport exceeds loadedBoundaries
        if (newCursorIndex >= loadedMin && newEndIndex <= loadedMax) {
            return; // All data already loaded
        }

        // Calculate range to load with prefetch buffer
        const buffer = Math.max(10, span);
        const rangeStart = Math.min(newCursorIndex - buffer, loadedMin);
        const rangeEnd = Math.max(newEndIndex + buffer, loadedMax);

        // Clamp to absolute boundaries (respects finite axes like resources)
        const [absoluteMin, absoluteMax] = axisInstance.getBoundaries();
        const clampedStart = Number.isFinite(absoluteMin)
            ? Math.max(rangeStart, absoluteMin)
            : rangeStart;
        const clampedEnd = Number.isFinite(absoluteMax)
            ? Math.min(rangeEnd, absoluteMax + 1)
            : rangeEnd;

        if (clampedStart >= clampedEnd) {
            return; // Nothing to load
        }

        // Start loading and track promise for debouncing
        const promise = axisInstance.ensureRange(clampedStart, clampedEnd);
        this.pendingEnsureRangePromises.set(key, promise);

        try {
            await promise;
        } finally {
            this.pendingEnsureRangePromises.delete(key);
        }
    }

    /**
     * Handle scroll events from grid-layout.
     *
     * Updates cursor position for all providers via StateManager.
     * Clamping is handled automatically by DataProviderStateManager.updateCursor().
     */
    private async handleLayoutScrolledAsync(e: CustomEvent<ScrollEventDetail>): Promise<void> {
        const {deltaX, deltaY, areaElement} = e.detail;

        if (!this.resolvedAxisConfigurations) {
            console.warn('No resolved axis configurations available. Cannot update cursor position.');
            return;
        }

        const stepsOfAxisX = this.updateRelativeDeltaOfAxis(areaElement, 'x', deltaX, this.resolvedAxisConfigurations, this.layoutTemplate);
        const stepsOfAxisY = this.updateRelativeDeltaOfAxis(areaElement, 'y', deltaY, this.resolvedAxisConfigurations, this.layoutTemplate);

        const {axisId: axisIdX, steps: stepsX, pendingCssWrites: cssWritesX} = stepsOfAxisX;
        const {axisId: axisIdY, steps: stepsY, pendingCssWrites: cssWritesY} = stepsOfAxisY;

        const keyOfAxisX = `${axisIdX}-x`;
        const keyOfAxisY = `${axisIdY}-y`;
        const isSteppingX = Math.abs(stepsX) >= 1;
        const isSteppingY = Math.abs(stepsY) >= 1;

        // Each axis is decided on its own, because one event can step on one axis
        // and merely shift on the other. Three cases per axis:
        //   (i)   it steps              -> hold the remainder back, write it when the window closes
        //   (ii)  no step, window open  -> hold it back too; writing now paints a
        //                                  remainder for a row the cursor has not reached
        //   (iii) no step, nothing open -> write at once, as before
        // Case (iii) is not an optimisation: without it the remainder of the
        // non-stepping axis of a mixed event is dropped entirely.
        const immediateCssWrites: PendingCssWrite[] = [];
        for (const [axisKey, isStepping, cssWrites] of [
            [keyOfAxisX, isSteppingX, cssWritesX] as const,
            [keyOfAxisY, isSteppingY, cssWritesY] as const,
        ]) {
            // An axis with `delta === 0` yields an empty list, and every vertical
            // wheel event has `deltaX === 0` exactly. An empty list must never
            // replace the slot, or the held remainder is lost.
            if (cssWrites.length === 0) {
                continue;
            }
            if (isStepping || this.openStepWindowCountByAxisKey.has(axisKey)) {
                this.remainderSlotByAxisKey.set(axisKey, cssWrites);
            } else {
                immediateCssWrites.push(...cssWrites);
            }
        }
        this.flushPendingCssWrites(immediateCssWrites);

        if (!isSteppingX && !isSteppingY) {
            return;
        }

        // One window per axis, so the two can close at different times. A mixed
        // event therefore produces one `cursor-changed` per axis rather than one
        // carrying both.
        await Promise.all([
            isSteppingX
                ? this.commitAxisStepsAsync(keyOfAxisX, axisIdX, 'x', stepsX, stepsOfAxisX.previousRelativeDelta)
                : Promise.resolve(),
            isSteppingY
                ? this.commitAxisStepsAsync(keyOfAxisY, axisIdY, 'y', stepsY, stepsOfAxisY.previousRelativeDelta)
                : Promise.resolve(),
        ]);

        // Update version to trigger Lit reactive update (avoids "scheduled update after update" warning)
        this.stateManagerVersion = this.dataProviderStateManager.version;
    }

    /**
     * Collects one event's row changes for an axis and commits them once that
     * axis' load window has closed.
     *
     * A window is open from the moment a row change starts waiting for its range
     * load until no load of that axis is outstanding any more. While it is open
     * this moves no cursor and writes no CSS: either on its own would let a frame
     * paint a cursor and a remainder that describe different positions, which is
     * exactly the jump this path exists to avoid. The event that closes the window
     * writes the newest remainder, moves the cursor by the SUM of everything
     * collected and dispatches a single `cursor-changed`.
     *
     * @param axisKey - `${axisId}-${axis}`, the same key the load map uses
     * @param axisId - the axis whose cursor is moved
     * @param axis - which of the two directions
     * @param steps - this event's row changes, signed
     * @param previousRelativeDelta - the delta as it was BEFORE this event advanced it
     */
    private async commitAxisStepsAsync(
        axisKey: string,
        axisId: string,
        axis: 'x' | 'y',
        steps: number,
        previousRelativeDelta: number
    ): Promise<void> {
        const openCount = this.openStepWindowCountByAxisKey.get(axisKey) ?? 0;
        if (openCount === 0) {
            // The first event of the window records what to fall back to if a load
            // fails. It has to be the value this event FOUND, because
            // updateRelativeDeltaOfAxis has already stored the advanced one.
            this.windowDeltaSnapshotByAxisKey.set(axisKey, previousRelativeDelta);
        }
        this.openStepWindowCountByAxisKey.set(axisKey, openCount + 1);
        this.collectedStepsByAxisKey.set(axisKey, (this.collectedStepsByAxisKey.get(axisKey) ?? 0) + steps);

        try {
            await this.ensureAxisRangeForScrollAsync(axisId, axis, this.collectedStepsByAxisKey.get(axisKey)!);
        } catch {
            // Tied to the counter, not to this event: another event may still be
            // counting on this window, so only the pass that closes it may reset.
            this.poisonedStepWindowAxisKeys.add(axisKey);
        }

        const remainingInWindow = (this.openStepWindowCountByAxisKey.get(axisKey) ?? 1) - 1;
        if (remainingInWindow > 0) {
            this.openStepWindowCountByAxisKey.set(axisKey, remainingInWindow);
            return;
        }
        this.openStepWindowCountByAxisKey.delete(axisKey);

        const collectedSteps = this.collectedStepsByAxisKey.get(axisKey) ?? 0;
        const remainderWrites = this.remainderSlotByAxisKey.get(axisKey) ?? [];
        const deltaSnapshot = this.windowDeltaSnapshotByAxisKey.get(axisKey);
        let windowFailed = this.poisonedStepWindowAxisKeys.delete(axisKey);
        this.collectedStepsByAxisKey.delete(axisKey);
        this.remainderSlotByAxisKey.delete(axisKey);
        this.windowDeltaSnapshotByAxisKey.delete(axisKey);

        // Every event in the window waited for the load that was running when it
        // arrived; the SUM can still reach past what that load covered. So the range
        // is re-checked against the sum before anything is committed.
        if (!windowFailed && collectedSteps !== 0) {
            try {
                await this.ensureAxisRangeForScrollAsync(axisId, axis, collectedSteps);
            } catch {
                windowFailed = true;
            }
        }

        if (windowFailed) {
            // Put the delta back where the window started and paint nothing. The
            // movement inside the window was never painted, so nothing visible is
            // lost; leaving the delta ahead of the cursor would instead offset every
            // later gesture by a whole row.
            if (deltaSnapshot !== undefined) {
                this.relativeDeltasByAxisId.set(axisId, deltaSnapshot);
            }
            return;
        }

        if (collectedSteps === 0) {
            // The row changes cancelled out, or an axis boundary absorbed them. The
            // remainder still has to reach the CSS.
            this.flushPendingCssWrites(remainderWrites);
            return;
        }

        // One synchronous block. Anything awaited between these three lines is a
        // frame that can paint a remainder belonging to a different cursor.
        this.flushPendingCssWrites(remainderWrites);
        this.updateCursorsByAxisId(axisId, axis, collectedSteps);
        this.dispatchCursorChanged([axisId]);
    }

    /**
     * Handles grid layout changes and updates span for all providers.
     * Calculates outer bounds of all areas and updates StateManager.
     */
    /**
     * Handles layout-size-changed event from o2d-layout.
     * Receives both simple dimension changes (no sectionsStartEndLines)
     * and grid recalculations (WITH sectionsStartEndLines when cell count changed).
     *
     * @param e - Event with dimensions and optionally updated sectionsStartEndLines
     */
    private handleLayoutSizeChanged(e: CustomEvent<LayoutSizeChangedDetail>): void {
        // T3.8: Conditional sectionsStartEndLines update
        // Only update if cell count changed (sectionsStartEndLines present in event detail)
        if (e.detail.sectionsStartEndLines) {
            // Deep-compare guard: skip redundant updates to prevent double axis rebuild on hard reload
            if (areSectionsStartEndLinesEqual(this.sectionsStartEndLines, e.detail.sectionsStartEndLines)) {
                return;
            }
            // Update state - willUpdate() cascade will handle area recalculation automatically
            this.sectionsStartEndLines = e.detail.sectionsStartEndLines;

            // T4.6: Reset scroll remainder on grid template change
            // KISS approach: reset ALL relativeDeltasByAxisId entries when cell count changes
            // Prevents stale subpixel offsets from old grid that don't match new grid dimensions
            for (const axisId of this.relativeDeltasByAxisId.keys()) {
                this.relativeDeltasByAxisId.set(axisId, O2dTimelineElement.INITIAL_RELATIVE_DELTA);
            }

            // Held-back remainders and collected row changes belong to the grid that
            // has just been replaced. A window still open will find an empty
            // collector and an empty slot and commit nothing, which is correct: the
            // deltas above were reset, so there is nothing left to commit.
            this.remainderSlotByAxisKey.clear();
            this.collectedStepsByAxisKey.clear();
            this.windowDeltaSnapshotByAxisKey.clear();
        }

        // Note: No explicit updateSpan() call needed!
        // willUpdate() detects sectionsStartEndLines change (line 343-347)
        // → calls calculateAreaCursorIndexesByDataProvider() for all providers
        // → areas re-render with updated areaSpanX/Y from new sectionsStartEndLines
    }

    /**
     * Handles the `gridConnected` event by updating the `sectionsStartEndLines` property
     * with the details provided in the event.
     *
     * @param {CustomEvent<Map<string, {startLine: number, endLine: number}>>} evt -
     * The custom event carrying a map where keys are section identifiers, and values are objects
     * containing `startLine` and `endLine` properties representing grid line positions.
     *
     * @return {Promise<void>} This method does not return a value.
     */
    private handleLayoutConnectedAsync(evt: O2DCustomEvent<{ sectionsStartEndLines: Map<string, StartEndLines> }>): void {
        this.sectionsStartEndLines = evt.detail.sectionsStartEndLines;
        // Note: Data preloading moved to axesReadyPromise.then() in willUpdate()
        // because on hard reload, layout-connected fires before DataProviders are created.
    }

    /**
     * Generates an array of resolved axis configurations by mapping the provided axis configurations
     * with their corresponding grid area configurations and applying the grid layout template.
     *
     * @param {AxisConfiguration<unknown>[]} axisConfigurations - An array of axis configuration objects describing the axis settings.
     * @param {AreaConfiguration[]} areaConfigurations - An array of area configuration objects used for grouping by grid section IDs.
     * @param {LayoutTemplate} layoutTemplate - The grid layout template used to transform and resolve the axis configurations.
     * @return {ResolvedAxisConfiguration<any>[]} An array of resolved axis configurations that are generated based on the provided input.
     */
    private generateAllResolvedAxisConfigurations(axisConfigurations: AxisConfiguration<unknown>[], areaConfigurations: AreaConfiguration[], layoutTemplate: LayoutTemplate): ResolvedAxisConfiguration<any>[] {

        // gruppiert jeder section ID passende AreaConfigurations zu
        const areaConfigurationBySectionId: Record<string, AreaConfiguration[]> = areaConfigurations.reduce((acc, config) => {
            const {rowSectionId, columnSectionId} = config.sectionIds;
            (acc[rowSectionId] ??= []).push(config);
            (acc[columnSectionId] ??= []).push(config);
            return acc;
        }, {} as Record<string, typeof areaConfigurations>);

        return axisConfigurations.map<ResolvedAxisConfiguration<any>>(axisConfiguration => this.generateResolvedAxisConfiguration(areaConfigurationBySectionId, axisConfiguration, layoutTemplate));
    }

    /**
     * Retrieves the layout section corresponding to the given ID or throws an error if not found.
     *
     * @param {Map<string, NormalizedSection>} normalizedSectionById - A map containing grid section IDs as keys and their corresponding normalized sections as values.
     * @param {string} sectionId - The ID of the grid section to retrieve.
     * @return {NormalizedSection} The normalized section associated with the provided grid section ID.
     * @throws {Error} Throws an error if the grid section ID is not found in the map.
     */
    private getLayoutSectionOrThrowError(normalizedSectionById: Map<string, NormalizedSection>, sectionId: string): NormalizedSection {
        if (!normalizedSectionById.has(sectionId)) {
            throw new Error(`Grid section with id ${sectionId} not found in grid layout template.`);
        }

        return normalizedSectionById.get(sectionId)!;
    }

    /**
     * Generates a resolved axis configuration containing information about the grid sections, grid area IDs, and the start cursor
     * associated with the provided axis configuration and grid layout template.
     *
     * @param {Record<string, AreaConfiguration[]>} areaConfigurationBySectionId - A mapping of grid section IDs to their respective area configurations.
     * @param {AxisConfiguration<unknown>} axisConfiguration - The axis configuration object defining axis properties and associated grid section IDs.
     * @param {LayoutTemplate} layoutTemplate - The template defining the structure and layout of the grid, including axis-specific sections.
     * @return {ResolvedAxisConfiguration<any>} A resolved configuration for the specified axis, containing additional details such as grid sections, grid area IDs, and the start cursor.
     */
    private generateResolvedAxisConfiguration(areaConfigurationBySectionId: Record<string, AreaConfiguration[]>, axisConfiguration: AxisConfiguration<unknown>, layoutTemplate: LayoutTemplate): ResolvedAxisConfiguration<any> {

        let areaIds: string[] = [];
        const sections = axisConfiguration.sectionIds.map<ExtendedSection>((sectionId: string) => {

            areaIds.push(...areaConfigurationBySectionId[sectionId]!.map(config => config.areaId));

            const sectionsOfAxisDirection: Section[] = axisConfiguration.axis === 'x'
                ? layoutTemplate.axisX
                : layoutTemplate.axisY;
            const sectionIndex = sectionsOfAxisDirection.findIndex(section => section.sectionId == sectionId);
            const sizeCssVariable = this.getLayoutSectionOrThrowError(this.normalizedSectionById, sectionId).section.sizeCssVariable;

            return {
                sectionId,
                sizeCssVariable,
                sectionIndex
            }
        });

        const startCursor = this.startCursors.find(startCursor => startCursor.axisId == axisConfiguration.axisId);
        if (!startCursor) {
            throw new Error(`Start cursor for axis ${axisConfiguration.axisId} not found.`);
        }

        return {
            ...axisConfiguration,
            sections,
            areaIds,
            startCursor
        }
    }


    /**
     * Builds a map of normalized grid layout sections by their unique identifiers.
     * This method processes sections from both the TXAxisValue and TYAxisValue axes of the provided grid layout template,
     * normalizes them with additional metadata, and stores them in a Map for easy access.
     *
     * @param {LayoutTemplate} layoutTemplateName The grid layout template containing the sections to be normalized.
     * The template includes section data for both the TXAxisValue and TYAxisValue axes.
     * @return {Map<string, NormalizedSection>} A Map where the keys are the unique identifiers of the grid sections, and the values
     * are the corresponding normalized grid layout sections, enriched with additional properties
     * such as name and axis information.
     */
    private buildNormalizedSectionsById(layoutTemplateName: LayoutTemplate): Map<string, NormalizedSection> {
        const {name} = layoutTemplateName;
        const sectionsOfAxisXById: Array<[string, NormalizedSection]> = layoutTemplateName.axisX.map(section => {
            const normalizedLayoutSection: NormalizedSection = {
                section: section,
                name,
                axis: 'x'
            }

            return [
                section.sectionId,
                normalizedLayoutSection
            ];
        });

        const sectionsOfAxisYById: Array<[string, NormalizedSection]> = layoutTemplateName.axisY.map(section => {
            const normalizedSection: NormalizedSection = {
                section,
                name,
                axis: 'y'
            }

            return [
                section.sectionId,
                normalizedSection
            ];
        });

        return new Map([...sectionsOfAxisXById, ...sectionsOfAxisYById]);
    }

    /**
     * Renders a grid area element based on the specified grid layout, area configuration, and data provider.
     *
     * @param {Map<string, StartEndLines>} sectionsStartEndLines - A map containing the start and end line definitions for each grid section.
     * @param {ResolvedAreaConfiguration} areaConfiguration - The configuration object for the grid area, including section IDs, data provider ID, and other related properties.
     * @return {HTMLTemplateResult} A template result representing the rendered grid area.
     */
    private renderArea(sectionsStartEndLines: Map<string, StartEndLines>, areaConfiguration: ResolvedAreaConfiguration): HTMLTemplateResult {

        const {columnSectionId, rowSectionId} = areaConfiguration.sectionIds;
        const dataProvider = this.dataProviderStateManager.getProvider(areaConfiguration.dataProviderId)!;

        const sectionStartEndLinesX = sectionsStartEndLines.get(columnSectionId);
        if (!sectionStartEndLinesX) {
            throw new Error(`Could not find column grid section with id ${columnSectionId} in grid layout template.`);
        }
        const areaSpanX = sectionStartEndLinesX.endLine - sectionStartEndLinesX.startLine;

        const sectionStartEndLinesY = sectionsStartEndLines.get(rowSectionId);
        if (!sectionStartEndLinesY) {
            throw new Error(`Could not find row grid section with id ${rowSectionId} in grid layout template.`);
        }
        const areaSpanY = sectionStartEndLinesY.endLine - sectionStartEndLinesY.startLine;

        const areaStyles: Readonly<StyleInfo> = {
            gridArea: `${sectionStartEndLinesY.startLine} / ${sectionStartEndLinesX.startLine} / span ${areaSpanY} / span ${areaSpanX}`,
        };

        const areaClasses: Readonly<ClassInfo> = {
            [areaConfiguration.dataProviderId.replace(':::', '-')]: true
        };

        const dataProviderState = this.dataProviderStateManager.getState(areaConfiguration.dataProviderId)!;
        const areaCursorIndexX = this.areaCursorIndexMaps.axisX.get(areaConfiguration.areaId)! + dataProviderState.cursorIndexX;
        const areaCursorIndexY = this.areaCursorIndexMaps.axisY.get(areaConfiguration.areaId)! + dataProviderState.cursorIndexY;

        return html`
            <o2d-area id=${areaConfiguration.areaId}
                      style=${styleMap(areaStyles)}
                      class=${classMap(areaClasses)}
                      .areaConfiguration=${areaConfiguration}
                      .dataProvider=${dataProvider}
                      .dataProviderVersion=${this.stateManagerVersion}
                      .areaCursorIndexX=${areaCursorIndexX}
                      .areaCursorIndexY=${areaCursorIndexY}
                      .areaSpanX=${areaSpanX}
                      .areaSpanY=${areaSpanY}
            ></o2d-area>`;
    }

    /**
     * Builds and returns a map of matrix axes based on the provided axis configurations.
     *
     * @param {AxisConfiguration<any>[]} resolvedAxisConfigurations - An array of resolved axis configuration objects used to define each axis.
     * @return {Map<string, Axis<any>>} A map where keys are axis IDs and values are matrix axis instances created using the configurations.
     */
    private buildAxes(resolvedAxisConfigurations: AxisConfiguration<any>[]): Map<string, Axis<any>> {
        const axes = new Map<string, Axis<any>>();
        const axisDataProviderFactory = new AxisDataProviderFactory();
        const axisFactory = new AxisFactory();

        for (const axisConfiguration of resolvedAxisConfigurations) {
            const axisDataProvider = axisDataProviderFactory.create(axisConfiguration.dataService);
            const startCursor = this.startCursors.find(startCursor => startCursor.axisId == axisConfiguration.axisId);
            axes.set(axisConfiguration.axisId, axisFactory.create(startCursor?.value, axisConfiguration, axisDataProvider));
        }

        return axes;
    }

    /**
     * Builds all necessary data providers based on the provided area configurations and axes mappings.
     * If a data provider for a specific combination of axes does not already exist, it will create one.
     *
     * Uses dataProviderConfigurations to look up configuration per axis combination.
     * Throws if no configuration found for a required axis combination.
     *
     * @param {ResolvedAreaConfiguration[]} resolvedAreaConfigurations - An array of resolved area configurations, each describing the TXAxisValue and TYAxisValue axis IDs for a specific area.
     * @param {Map<string, Axis<any>>} axes - A map of axis IDs to their corresponding Axis instances.
     * @return {{ key: string, provider: CrosspointDataProvider<any, any, any> }[]} List of newly registered providers (one entry per call to registerProvider).
     * @throws {Error} If dataProviderConfigurations is empty or no configuration found for axis combination.
     */
    private buildAllDataProviders(
        resolvedAreaConfigurations: ResolvedAreaConfiguration[],
        axes: Map<string, Axis<any>>
    ): { key: string, provider: CrosspointDataProvider<any, any, any> }[] {
        if (!this.dataProviderConfigurations || this.dataProviderConfigurations.length === 0) {
            throw new Error(
                'dataProviderConfigurations is required but empty. ' +
                'Provide at least one CrosspointDataProviderConfiguration for each unique axis combination.'
            );
        }

        const newProviderInfos: { key: string, provider: CrosspointDataProvider<any, any, any> }[] = [];

        for (const areaConfiguration of resolvedAreaConfigurations) {
            const {axisIdX, axisIdY} = areaConfiguration;

            if (!this.dataProviderStateManager.findDataProviderByAxesIds(axisIdX, axisIdY)) {
                const dataProviderId = buildDataProviderId(axisIdX, axisIdY);
                const config = this.findCrosspointDataProviderConfiguration(axisIdX, axisIdY);

                if (!config) {
                    throw new Error(
                        `No CrosspointDataProviderConfiguration found for axes "${axisIdX}" and "${axisIdY}". ` +
                        `Expected provider ID: "${dataProviderId}". ` +
                        `Available configurations: [${this.dataProviderConfigurations.map(c => buildDataProviderId(c.axisIdX, c.axisIdY)).join(', ')}]`
                    );
                }

                const provider = this.buildDataProvider(config, axes.get(axisIdX)!, axes.get(axisIdY)!, 0, 0);
                newProviderInfos.push({ key: dataProviderId, provider });
            }
        }

        return newProviderInfos;
    }

    /**
     * Find CrosspointDataProviderConfiguration by axis IDs
     *
     * @param {string} axisIdX - TXAxisValue-axis identifier
     * @param {string} axisIdY - TYAxisValue-axis identifier
     * @return {CrosspointDataProviderConfiguration | undefined} Matching configuration or undefined
     */
    private findCrosspointDataProviderConfiguration(
        axisIdX: string,
        axisIdY: string
    ): CrosspointDataProviderConfiguration<any, any, any> | undefined {
        return this.dataProviderConfigurations.find(
            config => config.axisIdX === axisIdX && config.axisIdY === axisIdY
        );
    }

    /**
     * Builds a data provider based on the provided configuration and matrix axes.
     *
     * Uses CrosspointDataServiceFactory to create the appropriate CrosspointDataService based on configuration type.
     *
     * @param {CrosspointDataProviderConfiguration} config - Configuration for the DataProvider
     * @param {Axis<any>} matrixAxisX - The matrix axis representing the TXAxisValue-axis.
     * @param {Axis<any>} matrixAxisY - The matrix axis representing the TYAxisValue-axis.
     * @param {number} spanX - The span value for the TXAxisValue-axis.
     * @param {number} spanY - The span value for the TYAxisValue-axis.
     * @return {CrosspointDataProvider<any, any, any>} A data provider configured with the specified configuration and axes.
     */
    private buildDataProvider(
        config: CrosspointDataProviderConfiguration<any, any, any>,
        matrixAxisX: Axis<any>,
        matrixAxisY: Axis<any>,
        spanX: number,
        spanY: number
    ): CrosspointDataProvider<any, any, any> {
        const spatialMatrix = new SpatialMatrix(matrixAxisX, matrixAxisY);
        const dataProviderId = buildDataProviderId(config.axisIdX, config.axisIdY);

        // Create CrosspointDataService using factory based on configuration type
        const dataService = CrosspointDataServiceFactory.create(
            config.itemDataService,
            matrixAxisX,
            matrixAxisY
        );

        // Use configured error handler or fallback to default
        const errorHandler = config.errorHandler ?? this.defaultErrorHandler;

        // Create callback for background-fetch completion to trigger re-render
        const onBackgroundFetchComplete = () => {
            this.dataProviderStateManager.notifyDataAvailable(dataProviderId);
            this.stateManagerVersion = this.dataProviderStateManager.version;
        };

        const dataProvider = new StandardDataProvider<any, any, any>(
            dataProviderId,
            spatialMatrix,
            dataService,
            config.itemMapper,
            errorHandler,
            onBackgroundFetchComplete
        );

        this.dataProviderStateManager.registerProvider(dataProvider.dataProviderKey, dataProvider, spanX, spanY);

        // NOTE: 'data-provider-ready' dispatch was moved out of this method (PRD-008).
        // Dispatching here fires before axesReadyPromise resolves, leading to consumers
        // calling scrollToValue()/ensureValue() on uninitialized axes. The event is now
        // dispatched centrally inside _runAxisSwapAsync() after the axes-ready barrier.

        return dataProvider;
    }

    /**
     * Generates a list of resolved area configurations by processing each area configuration
     * and combining it with the provided axis configurations.
     *
     * @param {AreaConfiguration[]} areaConfigurations - The list of area configurations to process.
     * @param {AxisConfiguration<any>[]} axisConfigurations - The list of axis configurations to use for resolution.
     * @return {ResolvedAreaConfiguration[]} A list of resolved area configurations derived from the input parameters.
     */
    private generateAllResolvedAreaConfigurations(areaConfigurations: AreaConfiguration[], axisConfigurations: AxisConfiguration<any>[]): ResolvedAreaConfiguration[] {
        return areaConfigurations.map(areaConfiguration => this.generateResolvedAreaConfiguration(areaConfiguration, axisConfigurations));
    }

    /**
     * Generates a resolved area configuration by enriching the provided area configuration
     * with axis details and a data provider ID based on the axis configurations.
     *
     * @param {AreaConfiguration} areaConfiguration The base area configuration object containing grid section IDs and other properties.
     * @param {AxisConfiguration<any>[]} axisConfigurations Array of axis configuration objects used to identify axis IDs and associate them with the area.
     * @return {ResolvedAreaConfiguration} A resolved area configuration object that includes enriched details such as axis IDs and the data provider ID.
     * @throws An error if the corresponding axis configuration for the column or row grid section cannot be found.
     */
    private generateResolvedAreaConfiguration(areaConfiguration: AreaConfiguration, axisConfigurations: AxisConfiguration<any>[]): ResolvedAreaConfiguration {
        const {columnSectionId, rowSectionId} = areaConfiguration.sectionIds;
        const resolvedAreaConfiguration: ResolvedAreaConfiguration = {
            ...areaConfiguration,
            cellRenderer: areaConfiguration.cellRenderer,
            sectionIds: {...areaConfiguration.sectionIds},
            axisIdX: 'N/A',
            axisIdY: 'N/A',
            dataProviderId: `N/A:::N/A`
        };

        const axisIdX = axisConfigurations.find(axisConfiguration => axisConfiguration.axis == 'x' && axisConfiguration.sectionIds.includes(columnSectionId))?.axisId
        if (!axisIdX) {
            throw new Error(`Could not find column axis configuration for column grid section with id ${columnSectionId}`);
        }
        resolvedAreaConfiguration.axisIdX = axisIdX;

        const axisIdY = axisConfigurations.find(axisConfiguration => axisConfiguration.axis == 'y' && axisConfiguration.sectionIds.includes(rowSectionId))?.axisId
        if (!axisIdY) {
            throw new Error(`Could not find column axis configuration for row grid section with id ${rowSectionId}`);
        }
        resolvedAreaConfiguration.axisIdY = axisIdY;

        resolvedAreaConfiguration.dataProviderId = `${axisIdX}:::${axisIdY}`;

        return resolvedAreaConfiguration;
    }

    /**
     * Applies buffered CSS remainder variable writes to the layout element synchronously.
     * Called after async data loading completes so CSS grid shift and content
     * update happen in the same frame (no microtask delay).
     *
     * @param {PendingCssWrite[]} pendingCssWrites - Buffered CSS variable writes to flush.
     */
    private flushPendingCssWrites(pendingCssWrites: PendingCssWrite[]): void {
        const layoutElement = this.renderRoot.querySelector('o2d-layout') as O2dLayoutElement | null;
        if (!layoutElement) return;
        for (const {prop, value} of pendingCssWrites) {
            DomHelper.setVariableValue(layoutElement, prop, value);
        }
    }

    /**
     * Updates the relative delta of a given axis based on the scrolling behavior and grid layout configuration.
     * CSS remainder writes are buffered (not applied) and returned for deferred application.
     *
     * @param {O2dAreaElement} areaElement - The area element representing a specific region of the grid.
     * @param {'x' | 'y'} axis - The axis to be updated, either 'x' for horizontal or 'y' for vertical.
     * @param {number} delta - The delta value that represents the scrolling offset along the specified axis.
     * @param {ResolvedAxisConfiguration<any>[]} resolvedAxisConfigurations - The list of resolved axis configurations that map grid sections to their respective axis identifiers.
     * @param {LayoutTemplate} layoutTemplate - The grid layout template that defines the structure and style of the grid layout.
     * @return {DataProviderBasedStep} A step containing the axis identifier, number of steps scrolled, and buffered CSS writes.
     */
    private updateRelativeDeltaOfAxis(
        areaElement: O2dAreaElement,
        axis: 'x' | 'y',
        delta: number,
        resolvedAxisConfigurations: ResolvedAxisConfiguration<any>[],
        layoutTemplate: LayoutTemplate
    ): DataProviderBasedStep {

        const sectionType = axis == 'x' ? `column` : `row`;
        const templateName = layoutTemplate.name;

        const {dataProvider, areaConfiguration} = areaElement;

        // Grid Section ID des Areas in dem das Scrollen startete in der aktuellen Achsen-Richtung (x oder y)
        const sectionId = sectionType === 'column'
            ? areaConfiguration.sectionIds.columnSectionId
            : areaConfiguration.sectionIds.rowSectionId;

        // ist die Achsen ID der Areas, in der das Scrollen startete
        const axisInstance = axis === 'x' ? dataProvider.axisX : dataProvider.axisY;
        const axisId = axisInstance.axisId;

        // No scroll on this axis - preserve existing CSS remainder state
        if (delta === 0) {
            return {axisId, steps: 0, pendingCssWrites: [], previousRelativeDelta: this.relativeDeltasByAxisId.get(axisId) ?? O2dTimelineElement.INITIAL_RELATIVE_DELTA};
        }

        // findet die Achsenkonfiguration für die Achsen ID der Areas statt, in der das Scrollen startete
        const resolvedAxisConfiguration = resolvedAxisConfigurations.find(axisConfig => axisConfig.axisId == axisId);
        if (!resolvedAxisConfiguration) {
            throw new Error(`Could not find axis configuration for axis ${axisId}`);
        }
        // Liste aller Grid Sections die zur Achsen ID gehören
        const allSectionsOfAxisId = resolvedAxisConfiguration.sections;

        // die findet in der Liste aller Grid Sections, die zur Achsen-ID gehören, die Grid Section, in der das Scrollen gestartet ist.
        const extendedSection = allSectionsOfAxisId.find(sectionOfAxis => sectionOfAxis.sectionId == sectionId);

        // holen von Zellgröße der Grid Section in der das Scrollen gestartet ist
        const sizeCssVariable = `${templateName}-${extendedSection!.sizeCssVariable}`;
        const sizeCssVariableValue = parseFloat(DomHelper.getComputedVariableValue(areaElement, sizeCssVariable));

        // Versatz beim Scrollen in Relation zur Zellgröße entlang der aktuellen Achse
        const relativeDeltaOfScrollEvent = delta / sizeCssVariableValue;

        // wenn ein relatives Delta noch nie verwendet wurde, müssen wir nun initial setzen
        if (!this.relativeDeltasByAxisId.has(axisId)) {
            this.relativeDeltasByAxisId.set(axisId, O2dTimelineElement.INITIAL_RELATIVE_DELTA);
        }

        let previousRelativeDelta = this.relativeDeltasByAxisId.get(axisId)!;
        let relativeDeltaOfAxis = previousRelativeDelta + relativeDeltaOfScrollEvent;

        let steps = this.getPassedIntegerSteps(previousRelativeDelta, relativeDeltaOfAxis);

        // The boundary is checked against everything the open window has collected
        // plus this event's own steps, not against this event alone: the cursor is
        // moved by the sum, so the sum is what can leave the axis.
        const collectedStepsOfWindow = this.collectedStepsByAxisKey.get(`${axisId}-${axis}`) ?? 0;
        const whichBoundaryIsCrossed = this.dataProviderStateManager.whichBoundaryIsCrossedAfterSteps(dataProvider.dataProviderKey, axis, collectedStepsOfWindow + steps);

        switch (whichBoundaryIsCrossed) {
            case 'min':
                relativeDeltaOfAxis = O2dTimelineElement.INITIAL_RELATIVE_DELTA;
                steps = 0;
                break;
            case 'max':
                relativeDeltaOfAxis = O2dTimelineElement.MAX_RELATIVE_DELTA;
                steps = 0;
                break;
            default:
                if (steps !== 0) {
                    relativeDeltaOfAxis += steps;
                }
        }

        this.relativeDeltasByAxisId.set(axisId, relativeDeltaOfAxis);

        // Buffer CSS remainder writes instead of applying immediately.
        // They will be flushed in handleLayoutScrolledAsync() AFTER async data loading,
        // so CSS grid shift and content update happen in the same frame.
        const pendingCssWrites: PendingCssWrite[] = [];
        for (const sectionOfAxisId of allSectionsOfAxisId) {

            // get remainder index value
            const sizeCssVariable = `${templateName}-${sectionOfAxisId.sizeCssVariable}`;
            const sizeCssVariableValueOfSection = parseFloat(DomHelper.getComputedVariableValue(areaElement, sizeCssVariable));

            // absolutes Delta ist der neue Remainder Wert für die Section
            if (relativeDeltaOfAxis < 0) {
                relativeDeltaOfAxis = 1 + relativeDeltaOfAxis;
            }

            const remainderOfSection = relativeDeltaOfAxis * sizeCssVariableValueOfSection;
            const sectionIndex = sectionOfAxisId.sectionIndex;
            const cssVariableRemainderName = `${axis}-remainder-${sectionIndex}`
            pendingCssWrites.push({prop: cssVariableRemainderName, value: `${remainderOfSection}px`});
        }

        return {axisId, steps, pendingCssWrites, previousRelativeDelta};
    }

    /**
     * Calculates the number of crossed integer steps (including signs) between two values.
     *
     * @param {number} prevValue - The previous numeric value.
     * @param {number} currentValue - The current numeric value.
     * @return {number} The number of crossed integer steps between the two values.
     */
    private getPassedIntegerSteps(prevValue: number, currentValue: number): number {
        // Returns how many integer boundaries were crossed, signed.
        //
        // Note the sign: the returned value is `floor(prev) - floor(current)`, the
        // OPPOSITE of the difference, in both branches. So a growing delta — the
        // picture moving down — yields NEGATIVE steps and a shrinking one yields
        // positive steps. A negative `deltaY` therefore moves the cursor UP in index
        // terms. The comments below used to claim the plain difference, which reads
        // as the opposite behaviour.
        if (currentValue > prevValue) {
            return Math.floor(prevValue) - Math.floor(currentValue);
        } else {
            // Abwärts: ceil(current) - ceil(prev)
            return Math.ceil(prevValue) - Math.ceil(currentValue);
        }
    }

    /**
     * Updates the cursors for all data providers associated with a given axis ID.
     *
     * @param {string} axisId - The identifier of the axis whose associated cursors need to be updated.
     * @param {'x'|'y'} axis - Specifies whether the update applies to the 'x' or 'y' axis.
     * @param {number} steps - The step increment or offset value used to adjust the cursors.
     * @return {void} No return value.
     */
    private updateCursorsByAxisId(axisId: string, axis: 'x' | 'y', steps: number): void {
        const dataProvidersUsingSameAxis = this.dataProviderStateManager.findDataProviderByAxisId(axisId);
        for (const dataProvider of dataProvidersUsingSameAxis) {
            this.dataProviderStateManager.updateCursorByAxisId(dataProvider.dataProviderKey, axis, steps);
        }
    }

    /**
     * Resolves the current domain values for the given axis IDs by reading the cursor index
     * from the state manager and mapping it to a domain value via Axis.getValue().
     * Skips axes without registered providers or with undefined values at the cursor index.
     *
     * @param axisIds - Axis IDs to resolve cursor values for
     * @returns Array of { axisId, value } entries — one per successfully resolved axis
     */
    private resolveCursorValues(axisIds: string[]): Array<{ axisId: string; value: unknown }> {
        const cursors: Array<{ axisId: string; value: unknown }> = [];
        for (const axisId of axisIds) {
            const providers = this.dataProviderStateManager.findDataProviderByAxisId(axisId);
            if (providers.length === 0) continue;
            const provider = providers[0];
            const state = this.dataProviderStateManager.getState(provider.dataProviderKey);
            if (!state) continue;
            const isX = provider.axisX.axisId === axisId;
            const axis = isX ? provider.axisX as Axis<unknown> : provider.axisY as Axis<unknown>;
            const cursorIndex = isX ? state.cursorIndexX : state.cursorIndexY;
            const value = axis.getValue(cursorIndex);
            if (value !== undefined) {
                cursors.push({ axisId, value });
            }
        }
        return cursors;
    }

    /**
     * Dispatches a `cursor-changed` custom event with the current domain values for the given axis IDs.
     * No-op if the element is not connected or if no axis IDs are provided.
     *
     * @param axisIds - Axis IDs whose cursor values should be included in the event detail
     */
    private dispatchCursorChanged(axisIds: string[]): void {
        if (!this.isConnected || axisIds.length === 0) return;
        const cursors = this.resolveCursorValues(axisIds);
        this.dispatchEvent(new CustomEvent<CursorChangedDetail>('cursor-changed', {
            bubbles: true,
            composed: true,
            detail: { cursors }
        }));
    }

    /**
     * Performs the actual cursor movement for each entry in the cursors list.
     * Called internally by applyCursorChangesAsync.
     * Resets relativeDeltasByAxisId AFTER all moves to avoid losing user scroll events between awaits.
     * Updates stateManagerVersion once at the end to trigger a single Lit re-render.
     *
     * @param cursors - Array of {axisId, value} entries to apply
     */
    private async _doApplyCursorChanges(cursors: ReadonlyArray<{ axisId: string; value: unknown }>): Promise<void> {
        const movedAxisIds: string[] = [];
        for (const cursor of cursors) {
            try {
                const moved = await this.scrollToValue(cursor.axisId, cursor.value, { skipNotify: true });
                if (moved) {
                    movedAxisIds.push(cursor.axisId);
                }
            } catch (e) {
                console.warn(`[o2d-timeline] applyCursorChangesAsync: scrollToValue failed for axis ${cursor.axisId}`, e);
            }
        }
        if (movedAxisIds.length === 0) return;

        // Reset sub-pixel offsets after all cursor moves complete (not between awaits)
        for (const axisId of movedAxisIds) {
            this.relativeDeltasByAxisId.set(axisId, O2dTimelineElement.INITIAL_RELATIVE_DELTA);
        }

        // Dispatch cursor-changed event once for all moves
        this.dispatchCursorChanged(movedAxisIds);

        // Single Lit re-render after all axes are updated
        this.stateManagerVersion = this.dataProviderStateManager.version;
    }

    /**
     * Serializes overlapping cursor-change requests via a pending-promise guard.
     * Rapid startCursors changes will await the previous call before starting.
     * This prevents race conditions when multiple Lit updates batch cursor changes.
     *
     * Waits for axesReadyPromise before applying to ensure axes are initialized
     * (protects the `else if (hasCursorChange)` path in willUpdate from firing
     * against not-yet-ready axes after a full rebuild).
     *
     * @param cursors - Array of {axisId, value} entries to apply
     */
    private async applyCursorChangesAsync(cursors: ReadonlyArray<{ axisId: string; value: unknown }>): Promise<void> {
        // Bind locally so a re-rebuild overwriting this.axesReadyPromise mid-await
        // does not leave us waiting on a stale promise.
        const currentAxesReady = this.axesReadyPromise;
        if (currentAxesReady) {
            await currentAxesReady;
        }
        if (this._pendingCursorApply) {
            await this._pendingCursorApply;
        }
        this._pendingCursorApply = this._doApplyCursorChanges(cursors);
        await this._pendingCursorApply;
        this._pendingCursorApply = null;
    }

    /**
     * Programmatically moves the cursor for a given axis to a specific value without triggering
     * a full axis/provider rebuild.
     *
     * Uses axis.ensureValue() for uncached targets to load data before moving the cursor.
     * Dispatches a 'cursor-changed' event and resets relativeDelta to prevent sub-pixel artifacts.
     *
     * @param axisId - The axis identifier to move
     * @param value - The target value to scroll to
    /**
     * Triggers a data refresh for all registered providers by incrementing the
     * DataProviderStateManager version. This causes a Lit re-render that propagates
     * the updated dataProviderVersion to all o2d-area elements via template bindings.
     *
     * Use this instead of directly manipulating o2d-area.dataProviderVersion,
     * which would be overwritten on the next Lit render cycle.
     */
    public requestDataRefresh(): void {
        for (const key of this.dataProviderStateManager.getAllProviderKeys()) {
            this.dataProviderStateManager.notifyDataAvailable(key);
        }
        this.stateManagerVersion = this.dataProviderStateManager.version;
    }

    /**
     * @param options - Optional settings. Use { skipNotify: true } when called from batch operations
     *                  (e.g., _doApplyCursorChanges) to defer side-effects to the caller.
     * @returns true if the cursor was moved successfully, false if no providers are registered,
     *          no state is available, or the target value could not be loaded
     */
    public async scrollToValue(axisId: string, value: unknown, options?: { skipNotify?: boolean }): Promise<boolean> {
        const providers = this.dataProviderStateManager.findDataProviderByAxisId(axisId);
        if (providers.length === 0) return false;

        const firstProvider = providers[0];
        const axisDirection: 'x' | 'y' = firstProvider.axisX.axisId === axisId ? 'x' : 'y';
        const axisInstance: Axis<any> = axisDirection === 'x' ? firstProvider.axisX as Axis<any> : firstProvider.axisY as Axis<any>;

        // Resolve target index — use ensureValue() for uncached targets
        let targetIndex = axisInstance.getIndex(value as never);
        if (targetIndex === undefined) {
            try {
                targetIndex = await axisInstance.ensureValue(value as never);
            } catch {
                return false;
            }
        }

        // Get current cursor index for this axis
        const state = this.dataProviderStateManager.getState(firstProvider.dataProviderKey);
        if (!state) return false;

        const currentCursorIndex = axisDirection === 'x' ? state.cursorIndexX : state.cursorIndexY;
        const delta = targetIndex - currentCursorIndex;

        if (delta === 0) return true;

        this.updateCursorsByAxisId(axisId, axisDirection, delta);

        if (!options?.skipNotify) {
            // Reset sub-pixel offset to prevent visual artifacts from stale remainder
            this.relativeDeltasByAxisId.set(axisId, O2dTimelineElement.INITIAL_RELATIVE_DELTA);

            // Dispatch cursor-changed event so consumers can react to programmatic cursor moves
            this.dispatchCursorChanged([axisId]);

            // Trigger Lit reactive update
            this.stateManagerVersion = this.dataProviderStateManager.version;
        }

        return true;
    }

}